<script setup lang="ts">
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { notify } from '@kyvg/vue3-notification'

import { useFileDownloader } from '@/hooks/use-file-downloader'
import { useAuthStore } from '@/stores/auth-store'
import { MyDateRangePickerRef, PaginatedResponse, uuid } from '@/types/general'
import { MinimalLoadingList } from '@/types/cargo-loading'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { GetDataParameters } from '@/types/table'
import { datetime } from '@/utils/dates'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { nameAndShortCodeString } from '@/utils/cargo-loading'

import ManageLoadingListModal from '@/components/cargo-loading/ManageLoadingListModal.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'

interface FetchLoadingListParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const authStore = useAuthStore()
const { t } = useI18n()
const route = useRoute()
const { downloading, downloadFile } = useFileDownloader()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalLoadingList,
    FetchLoadingListParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<MinimalLoadingList>>(
            window.route('cl.company.loading-lists.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            'start-date': dayjs().startOf('day').toDate().toISOString(),
            'end-date': dayjs().add(2, 'days').endOf('day').toDate().toISOString(),
        },
    },
)

const modalOpen = ref(false)
const selectedLoadingListId = ref<uuid | null>(null)
const dateRangePickerRef = ref<MyDateRangePickerRef>()

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}

function openEditModal(id: uuid | null) {
    selectedLoadingListId.value = id
    modalOpen.value = true
}

function downloadLoadingLists() {
    if (paginationData.value.total === 0) {
        notify({ type: 'error', text: t('noEntityToPrint', { entity: t('loadingLists') }) })
        return
    }

    if (paginationData.value.total > 100) {
        notify({ type: 'error', text: t('maximumEntityExceeded', { entity: t('loadingLists') }) })
        return
    }

    downloadFile(
        window.route('cl.loading-lists.download-multiple', {
            'company-id': authStore.companyId,
            'start-date': params.value.filter['start-date'],
            'end-date': params.value.filter['end-date'],
        }),
    )
}

function closeModal() {
    modalOpen.value = false
    selectedLoadingListId.value = null
}

useMittListener('fetchLoadingLists', () => refetch())
useMittListener('printLoadingLists', () => downloadLoadingLists())
</script>

<template>
    <MyTable
        table-id="cargo-loading"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading || downloading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('cargoList')"
    >
        <template #filters>
            <MyDateRangePicker
                ref="dateRangePickerRef"
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />
        </template>

        <template #actionRow="{ row }">
            <RouterLink
                :to="{
                    name: 'cl.loading-lists.show',
                    params: { listId: row.id },
                    query: route.query,
                }"
            >
                <ActionRowItem>
                    <mdi:eye />
                </ActionRowItem>
            </RouterLink>
        </template>

        <template #actionMenu="{ row }">
            <ActionMenuItem @click="openEditModal(row.id)">
                <span class="mr-2"><mdi:pencil /></span>
                {{ t('edit') }}
            </ActionMenuItem>
        </template>

        <MyTableColumn
            :name="t('provider')"
            property="provider.name"
            :tooltip="t('providerExplanation')"
        />
        <template #[`provider.name`]="{ row }">
            {{ nameAndShortCodeString(row.provider.name, row.providerAccountNumber) }}
        </template>
        <MyTableColumn :name="t('providerNumber')" property="providerNumber" />
        <MyTableColumn
            :name="t('tripNumber')"
            property="tripNumber"
            :tooltip="t('tripNumberExplanation')"
        />
        <MyTableColumn
            :name="t('departedAt')"
            property="departedAt"
            :tooltip="t('departedAtExplanation')"
        />
        <template #departedAt="{ row }">
            {{ datetime(row.departedAt) }}
        </template>
        <MyTableColumn
            :name="t('loadedAt')"
            property="loadedAt"
            :tooltip="t('loadedAtExplanation')"
        />
        <template #loadedAt="{ row }">
            {{ datetime(row.loadedAt) }}
        </template>
        <MyTableColumn
            :name="t('truckNumber')"
            property="truckNumber"
            :tooltip="t('truckNumberExplanation')"
        />
        <MyTableColumn
            :name="t('trailerNumber')"
            property="trailerNumber"
            :tooltip="t('trailerNumberExplanation')"
        />
        <MyTableColumn
            :name="t('flightNumber')"
            property="flightNumber"
            :tooltip="t('flightNumberExplanation')"
        />
        <MyTableColumn :name="t('origin')" property="originPort.shortName" />
        <MyTableColumn :name="t('destinations')" property="destinationPorts" :sortable="false" />
        <template #destinationPorts="{ row }">
            {{ row.destinationPorts.map((port) => port.shortName).join(',') }}
        </template>
        <MyTableColumn :name="t('country')" property="country" :tooltip="t('countryExplanation')" />
        <MyTableColumn :name="t('user')" property="user.name" :tooltip="t('user')" />
        <MyTableColumn
            :name="t('routes')"
            property="routesCount"
            :tooltip="t('routesExplanation')"
        />
        <MyTableColumn :name="t('items')" property="itemsCount" :tooltip="t('itemsExplanation')" />
    </MyTable>

    <ManageLoadingListModal
        v-if="modalOpen"
        :id="selectedLoadingListId"
        v-model="modalOpen"
        @close="closeModal()"
        @saved="refetch"
    />
</template>
